/**
 * Styles shared by the routed pages, composed into each shadow root:
 *
 *     static styles = [pageStyles, css`…what only this page needs…`];
 *
 * Markup contract of a page:
 *
 *     <nav class="crumbs">…</nav>          ← optional
 *     <header class="page-head">
 *       <div><h1>…</h1><p class="lead">…</p></div>
 *       <div class="actions">…</div>      ← optional
 *     </header>
 *     <section>…<h2>…</h2>…</section>
 *
 * A title with an explanation behind a "?":
 *
 *     <div class="page-title"><h1>…</h1><plane-help topic="…">…</plane-help></div>
 */
import { css } from 'lit';

import { phone, touchOrNarrow } from '../media.js';

export const pageStyles = css`
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  /* The sections of a page are numbered, in the order they come. */
  :host {
    display: block;
    counter-reset: section;
  }

  /* A link reads as one without its colour: underlined, the line heavier under the pointer. */
  a {
    color: var(--link);
    text-decoration: underline;
    text-decoration-thickness: var(--border-width);
    text-decoration-color: color-mix(in srgb, currentColor 40%, transparent);
    text-underline-offset: var(--link-underline-offset);
  }

  a:hover {
    text-decoration-color: currentColor;
    text-decoration-thickness: var(--rule-width);
  }

  code,
  .mono {
    font-family: var(--font-mono);
    font-size: var(--font-size-code);
    letter-spacing: var(--tracking-tight);
  }

  .crumbs {
    margin-bottom: var(--space-3);
    color: var(--text-muted);
    font-family: var(--font-mono);
    font-size: var(--font-size-xs);
    letter-spacing: var(--tracking-wide);
    text-transform: uppercase;
  }

  .crumbs a {
    color: var(--text);
  }

  /* A taller hit area on a touch screen, the line itself unchanged. */
  @media ${touchOrNarrow} {
    .crumbs a {
      display: inline-block;
      padding-block: var(--space-3);
      margin-block: calc(var(--space-3) * -1);
    }
  }

  .crumbs a:hover {
    color: var(--accent);
  }

  .page-head {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    justify-content: space-between;
    gap: var(--space-4);
    margin-bottom: var(--section-gap);
  }

  h1 {
    margin: 0;
    font-family: var(--font-display);
    font-weight: var(--font-display-weight);
    font-size: var(--font-size-title);
    line-height: var(--line-height-tight);
    letter-spacing: var(--font-display-tracking);
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--space-3);
  }

  /* A title with its "?" (plane-help) beside it. */
  .page-title {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--space-3);
  }

  h1 code {
    font-size: var(--font-size-title-code);
    font-weight: var(--font-weight-medium);
    letter-spacing: var(--tracking-tight);
  }

  /* Section titles: the grotesque, bold, sized by weight and not by colour. */
  h2 {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: var(--space-2) var(--space-3);
    margin: 0 0 var(--space-4);
    font-family: var(--font-display);
    font-size: var(--font-size-lg);
    font-weight: var(--font-weight-bold);
    letter-spacing: var(--tracking-tight);
    line-height: var(--line-height-snug);
    color: var(--text);
  }

  /* A button set in a heading (Edit, Change bot) goes to the far end of the line. */
  h2 button,
  h2 .button {
    margin-left: auto;
    align-self: center;
    letter-spacing: normal;
  }

  /* The section of a page, as on a sign: its number, a heavy rule above it. */
  section > h2 {
    padding-top: var(--space-3);
    border-top: var(--rule-width) solid var(--text);
  }

  section > h2::before {
    counter-increment: section;
    content: counter(section, decimal-leading-zero);
    min-width: 1.75rem;
    font-family: var(--font-mono);
    font-size: var(--font-size-xs);
    font-weight: var(--font-weight-medium);
    letter-spacing: var(--tracking-wide);
    color: var(--accent);
  }

  section + section {
    margin-top: var(--section-gap);
  }

  .lead {
    margin: var(--space-2) 0 0;
    max-width: var(--prose-width);
    color: var(--text-muted);
    font-size: var(--font-size-lg);
    line-height: var(--line-height-ui);
  }

  .muted {
    color: var(--text-muted);
  }

  .actions {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
  }

  .empty {
    margin: 0;
    padding: var(--space-5) var(--space-4);
    border: var(--border-width) dashed var(--border-strong);
    border-radius: var(--radius);
    color: var(--text-muted);
  }

  .card {
    padding: var(--space-4) var(--space-5);
    border: var(--border-width) solid var(--border);
    border-radius: var(--radius);
    background: var(--surface-raised);
  }

  /* Cards that follow each other in a section. */
  section > .card + .card {
    margin-top: var(--card-gap);
  }

  /* What cannot be undone (actions that remove, revoke, delete): apart, at the bottom of a page,
     in the danger tone. Markup: .card.danger-zone > h3 + .danger-action > p > strong + button. */
  .danger-zone {
    border-color: var(--tone-danger);
    border-left-width: calc(var(--border-width) * 4);
    background: color-mix(in srgb, var(--tone-danger-soft) 40%, var(--surface-raised));
  }

  .danger-zone h3 {
    margin: 0 0 var(--space-3);
    font-family: var(--font-mono);
    font-size: var(--font-size-2xs);
    font-weight: var(--font-weight-semibold);
    text-transform: uppercase;
    letter-spacing: var(--tracking-widest);
    color: var(--tone-danger);
  }

  .danger-action {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3);
  }

  .danger-action + .danger-action {
    margin-top: var(--space-3);
    padding-top: var(--space-3);
    border-top: var(--border-width) solid var(--border);
  }

  .danger-action p {
    flex: 1 1 var(--inline-field-min-width);
    margin: 0;
  }

  .danger-action strong {
    display: block;
  }

  .grid {
    display: grid;
    gap: var(--card-gap);
    grid-template-columns: repeat(auto-fit, minmax(var(--card-min-width), 1fr));
  }

  /* A definition list laid out as label / value rows. The label column is as wide as its longest
     label up to 40% of the list: on a phone, a long label ("Who can write to it") wraps instead
     of squeezing the values into a column a word wide. */
  dl.facts {
    display: grid;
    grid-template-columns: fit-content(40%) minmax(0, 1fr);
    gap: var(--space-2) var(--space-5);
    margin: 0;
  }

  @media ${phone} {
    dl.facts {
      column-gap: var(--space-3);
    }
  }

  dl.facts dt {
    color: var(--text-muted);
    font-family: var(--font-mono);
    font-size: var(--font-size-xs);
    letter-spacing: var(--tracking-wide);
    text-transform: uppercase;
    padding-top: var(--space-0);
  }

  dl.facts dd {
    margin: 0;
    min-width: 0;
    /* Not "anywhere": that also lets the column shrink until every word breaks between letters.
       This breaks only what would overflow anyway, a digest or a long id. */
    overflow-wrap: break-word;
  }

  .notice {
    position: relative;
    padding: var(--space-3) var(--space-4) var(--space-3) calc(var(--space-4) + var(--edge-width));
    border-radius: var(--radius);
    border: var(--border-width) solid var(--border);
    margin-bottom: var(--space-4);
    background: var(--surface-raised);
    overflow: hidden;
  }

  .notice::before {
    content: '';
    position: absolute;
    inset: 0 auto 0 0;
    width: var(--edge-width);
    background: var(--notice-edge, var(--border-strong));
  }

  .notice strong {
    display: block;
  }

  .notice[data-tone='danger'] {
    --notice-edge: var(--tone-danger);
    background: var(--tone-danger-soft);
    border-color: color-mix(in srgb, var(--tone-danger) var(--tone-border-mix), transparent);
  }

  .notice[data-tone='warning'] {
    --notice-edge: var(--tone-warning);
    background: var(--tone-warning-soft);
    border-color: color-mix(in srgb, var(--tone-warning) var(--tone-border-mix), transparent);
  }

  .notice[data-tone='progress'] {
    --notice-edge: var(--tone-progress);
    background: var(--tone-progress-soft);
    border-color: color-mix(in srgb, var(--tone-progress) var(--tone-border-mix), transparent);
  }

  .notice[data-tone='muted'],
  .notice[data-tone='ok'] {
    background: var(--surface);
    border-color: var(--border);
  }
`;
